import { z } from 'zod'

const requiredMessage = "Ce champ est requis"

export enum QualityEnum {
  ARTIST = "artist",
  AUTHOR = "author",
  COMPOSER = "composer",
  ARRANGER = "arranger",
  EDITOR = "editor"
}

export enum WorkTypeEnum {
  NEW = 'new',
  EXISTING = 'existing',
  SIGNATURE = 'signature',
}

export enum MediaEnum {
  RADIO = 'RADIO',
  TV = 'TV',
}

const BeneficiarySchema = z.object({
  beneficiary_id: z.string().readonly(),
  names: z.string().min(1, requiredMessage),
  qualities: z.array(z.nativeEnum(QualityEnum)).optional()
})

export const FormulaireSchema = z.object({
  spot: z.object({
    idCreaCode: z.string().optional(),
    brand: z.string().min(1, requiredMessage),
    productName: z.string().min(1, requiredMessage),
    productNature: z.string().optional(),
    declarant: z.string().optional(),
    spotTitle: z.string().min(1, requiredMessage),
    duration: z.string().min(1, requiredMessage),
    arppCodes: z.string().optional(),
    pubIdCodes: z.string().optional(),
    spotLink: z.string().readonly(),
  }),
  first_diffusion: z.object({
    media: z.nativeEnum(MediaEnum, {
      errorMap: (issue, _ctx) => {
        switch (issue.code) {
          case 'invalid_type':
            return { message: 'Le type de média est requis.' };
          case 'invalid_enum_value':
            return { message: 'Le type de média est requis.' };
          default:
            return { message: 'Le type de média est requis.' };
        }
      },
    }),
    broadcaster: z.string().min(1, requiredMessage),
    dateTime: z.string().min(1, requiredMessage),
  }),
  works: z.array(z.object({
    work_id: z.number().nullable().readonly().optional(),
    title: z.string().min(1, requiredMessage),
    usedInSpotDuration: z.string().min(1, requiredMessage).refine((value) => {
      const duration = parseFloat(value)
      return !isNaN(duration) && duration > 0
    }, {
      message: "La durée utilisée dans le spot doit être > 0"
    }),
    workType: z.nativeEnum(WorkTypeEnum, {
      errorMap: (issue, _ctx) => {
        switch (issue.code) {
          case 'invalid_type':
            return { message: 'Le type d\'œuvre est requis.' };
          case 'invalid_enum_value':
            return { message: 'Le type d\'œuvre est requis.' };
          default:
            return { message: 'Le type d\'œuvre est requis.' };
        }
      },
    }),
    iswcCode: z.string().optional(),
    isrcCode: z.string().optional(),
    workCode: z.string().optional(),
    beneficiaries: z.array(BeneficiarySchema).max(10, "Maximum 10 ayants droit"),
    audioFile: z.any(z.instanceof(File).optional()).optional(),
    file_name: z.string().readonly().optional()
  })).min(1, "Au moins une œuvre est requise").max(10),
  declarant: z.object({
    id: z.number().readonly(),
    last_edition_date: z.string().readonly(),
    last_name: z.string().readonly(),
    first_name: z.string().readonly(),
    company_name: z.string().readonly()
  }),
  should_send_to_sacem: z.boolean().optional()
}).superRefine((data, ctx) => {

  //total of usedInSpotDuration must be in spot duration
  const totalUsedInSpotDuration = data.works.reduce((acc, work) => acc + parseFloat(work.usedInSpotDuration), 0)
  if (totalUsedInSpotDuration > parseFloat(data.spot.duration)) {
    // Add error on each usedInSpotDuration field
    data.works.forEach((work, index) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La durée totale des œuvres utilisées dans le spot ne peut pas dépasser la durée du spot",
        path: [...ctx.path, 'works', index, 'usedInSpotDuration'],
      });
    });
  }

  //qualities required if beneficiary has name
  data.works.forEach((work, workIndex) => {
    work.beneficiaries?.forEach((beneficiary, beneficiaryIndex) => {
      if (beneficiary.names) {
        if (!beneficiary.qualities || beneficiary.qualities.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Les qualités sont requises pour un bénéficiaire",
            path: [...ctx.path, 'works', workIndex, 'beneficiaries', beneficiaryIndex, 'qualities'],
          });
        }
      }
    });
  })

  //at least one author or composer and one editor per work
  data.works.forEach((work, workIndex) => {
    const workAuthors = work.beneficiaries.filter((beneficiary) => beneficiary.qualities?.includes(QualityEnum.AUTHOR)).length;
    const workEditors = work.beneficiaries.filter((beneficiary) => beneficiary.qualities?.includes(QualityEnum.EDITOR)).length;
    const workComposers = work.beneficiaries.filter((beneficiary) => beneficiary.qualities?.includes(QualityEnum.COMPOSER)).length;

    if ((workAuthors < 1 && workComposers < 1) || workEditors < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Au moins un auteur/compositeur et un éditeur sont requis pour cette œuvre",
        path: [...ctx.path, 'works', workIndex, 'beneficiaries'],
      });
    }
  });
})

export type FormulaireSchemaType = z.infer<typeof FormulaireSchema>
export type BeneficiarySchemaType = z.infer<typeof BeneficiarySchema>